import produce, { Draft } from 'immer';

import {
  IAddPurchaseOrder,
  IAddUserBillingInfo,
  ICancelPaypalOrder,
  ICloseManagementOrderPopup,
  IConfirmPurchaseOrder,
  ISetActiveSalesPackage,
  ISetCountryRegulations,
  ISetCountryRegulationsFetching,
  ISetFetchingSubscriptionPlans,
  ISetManagementIssuedOrder,
  ISetManagementIssuedOrderFetching,
  ISetPayerType,
  ISetPayerVatNumber,
  ISetPayerVatRegistered,
  ISetPaymentBillingInfo,
  ISetPaymentCountry,
  ISetPurchaseOrders,
  ISetPurchaseOrdersFetching,
  ISetPurchasingManagementIssuedOrder,
  ISetSubscriptionPlans,
  ISetUserBillingInfo,
  ISetUserBillingInfoFetching,
  ISetVatCalculations,
  ISetVatVerificationResponse,
  IUpdatePurchaseOrder,
  IUpdateUserBillingInfo,
} from '../../actions/billing';
import * as actionTypes from '../../constants/actionTypes';
import {
  existingOrderToSubscriptionPlan,
  getActiveSalesPackageFromLocation,
} from '../../lib/billing';
import { Payer, UserBillingInfoDto } from '../../meta/types/billing/billingInfo';
import { Order, OrderStatus } from '../../meta/types/billing/order';
import { BILLING_PLAN_MANAGEMENT_OFFER, SalesPackage } from '../../meta/types/billing/salespackage';
import {
  CountryRegulation,
  VatCalculateResponse,
  VatVerificationResponse,
} from '../../meta/types/billing/vat';

export const initialState = Object.freeze({
  countryRegulations: [] as CountryRegulation[],
  fetchingCountryRegulations: false,

  purchaseOrders: [] as Order[],
  fetchingPurchaseOrders: false,
  purchaseOrdersFetched: false,

  fetchingSubscriptionPlans: false,
  salesPackages: [] as SalesPackage[],

  fetchingUserBillingInfo: false,
  userBillingInfoFetched: false,
  userBillingInfo: [] as UserBillingInfoDto[],

  fetchingManagementIssuedOrder: false,
  managementOfferFetched: false,
  managementOrderPopupClosed: false,

  // Billing flow,
  activeSalesPackage: getActiveSalesPackageFromLocation(),
  payerVatRegistered: true,
  payerVatNumber: '',
  isPurchasingManagementIssuedOrder: false,
});

export type BillingState = typeof initialState & {
  managementIssuedOrder?: Order;
  payerType?: Payer;
  paymentCountry?: string;
  vatVerificationResponse?: VatVerificationResponse;
  paymentBillingInfo?: UserBillingInfoDto;
  vatCalculations?: VatCalculateResponse;
};

type BillingAction =
  | ISetUserBillingInfo
  | ISetUserBillingInfoFetching
  | IAddUserBillingInfo
  | IAddPurchaseOrder
  | IConfirmPurchaseOrder
  | ICancelPaypalOrder
  | ISetManagementIssuedOrderFetching
  | ISetManagementIssuedOrder
  | ICloseManagementOrderPopup
  | IUpdateUserBillingInfo
  | ISetCountryRegulations
  | ISetCountryRegulationsFetching
  | ISetPurchaseOrders
  | ISetPurchaseOrdersFetching
  | ISetFetchingSubscriptionPlans
  | IUpdatePurchaseOrder
  | ISetSubscriptionPlans
  | ISetActiveSalesPackage
  | ISetPaymentCountry
  | ISetPayerVatRegistered
  | ISetPayerVatNumber
  | ISetVatVerificationResponse
  | ISetPaymentBillingInfo
  | ISetVatCalculations
  | ISetPurchasingManagementIssuedOrder
  | ISetPayerType;

const billing = (state: BillingState = initialState, action: BillingAction) => {
  return produce<BillingState>(state, (draft: Draft<BillingState>) => {
    switch (action.type) {
      case actionTypes.SET_PURCHASING_MANAGEMENT_ISSUED_ORDER:
        if (state.managementIssuedOrder) {
          draft.isPurchasingManagementIssuedOrder = action.purchasingManagementIssuedOrder;
          draft.activeSalesPackage = {
            salesPackageId: BILLING_PLAN_MANAGEMENT_OFFER,
            paymentStrategyId: 0,
          };
          draft.managementOrderPopupClosed = true;
        } else {
          draft.isPurchasingManagementIssuedOrder = false;
        }
        return;
      case actionTypes.SET_VAT_CALCULATIONS:
        draft.vatCalculations = action.vatCalculations;
        return;
      case actionTypes.SET_PAYMENT_BILLING_INFO:
        const ubi = action.userBillingInfo;
        if (ubi) {
          if (!state.userBillingInfo.find(existingUbi => existingUbi.id === ubi.id)) {
            draft.userBillingInfo = [...state.userBillingInfo, ubi];
          }

          if (ubi.billingInfo.vatNumber && ubi.billingInfo.type !== undefined) {
            draft.payerType = ubi.billingInfo.type;
            draft.payerVatNumber = ubi.billingInfo.vatNumber;
            draft.payerVatRegistered = true;
          } else {
            draft.payerType = Payer.INDIVIDUAL;
            draft.payerVatNumber = '';
            draft.payerVatRegistered = false;
          }
          draft.paymentCountry = ubi.billingInfo.countryCode;
        }
        draft.paymentBillingInfo = action.userBillingInfo;
        return;
      case actionTypes.SET_VAT_VERIFICATION_RESPONSE:
        draft.vatVerificationResponse = action.vatVerification;
        return;
      case actionTypes.SET_PAYER_VAT_NUMBER:
        draft.payerVatNumber = action.vatNumber;
        return;
      case actionTypes.SET_PAYER_VAT_REGISTERED:
        draft.payerVatRegistered = action.vatRegistered;
        return;
      case actionTypes.SET_PAYMENT_COUNTRY:
        draft.paymentCountry = action.paymentCountry;
        return;
      case actionTypes.SET_PAYER_TYPE:
        draft.payerType = action.payerType;
        return;
      case actionTypes.SET_ACTIVE_SALES_PACKAGE:
        draft.activeSalesPackage = action.selector;
        return;
      case actionTypes.CONFIRM_PURCHASE_ORDER:
        draft.managementIssuedOrder = undefined;
        draft.purchaseOrders = state.purchaseOrders.map(o =>
          o.id === action.order.id ? action.order : o,
        );
        return;
      case actionTypes.UPDATE_PURCHASE_ORDER:
        draft.purchaseOrders = state.purchaseOrders.map(o =>
          o.id === action.order.id ? action.order : o,
        );
        return;
      case actionTypes.SET_COUNTRY_REGULATIONS:
        draft.fetchingCountryRegulations = false;
        draft.countryRegulations = action.countryRegulations;
        return;
      case actionTypes.SET_COUNTRY_REGULATIONS_FETCHING:
        draft.fetchingCountryRegulations = action.fetching;
        return;
      case actionTypes.SET_PURCHASE_ORDERS:
        draft.purchaseOrders = action.orders;
        draft.fetchingPurchaseOrders = false;
        draft.purchaseOrdersFetched = true;
        return;
      case actionTypes.SET_PURCHASE_ORDERS_FETCHING:
        draft.fetchingPurchaseOrders = action.fetching;
        return;
      case actionTypes.CANCEL_PAYPAL_ORDER:
        draft.purchaseOrders = draft.purchaseOrders.map(o =>
          o.paymentSystemId === action.paypalPaymentId ? { ...o, status: OrderStatus.CANCELED } : o,
        );
        return;
      case actionTypes.ADD_PURCAHSE_ORDER:
        draft.purchaseOrders = [action.order, ...draft.purchaseOrders];
        return;
      case actionTypes.SET_MANAGEMENT_ISSUED_ORDER:
        if (action.order) {
          const subscriptionPlans = [
            ...state.salesPackages,
            existingOrderToSubscriptionPlan(action.order),
          ];
          draft.salesPackages = subscriptionPlans;
        }
        draft.fetchingManagementIssuedOrder = false;
        draft.managementIssuedOrder = action.order;
        draft.managementOfferFetched = true;
        return;
      case actionTypes.SET_SUBSCRIPTION_PLANS:
        draft.salesPackages = action.plans;
        draft.fetchingSubscriptionPlans = false;
        return;
      case actionTypes.SET_FETCHING_SUBSCRIPTION_PLANS:
        draft.fetchingSubscriptionPlans = action.fetching;
        return;
      case actionTypes.CLOSE_MANAGEMENT_ORDER_POPUP:
        draft.managementOrderPopupClosed = true;
        return;
      case actionTypes.SET_MANAGEMENT_ISSUED_ORDER_FETCHING:
        const managementOfferFetched =
          action.fetching === false ? true : state.managementOfferFetched;

        return {
          ...state,
          fetchingManagementIssuedOrder: action.fetching,
          managementOfferFetched,
        };
      case actionTypes.UPDATE_USER_BILLING_INFO:
        draft.userBillingInfo = state.userBillingInfo.map(bi =>
          bi.id === action.userBillingInfo.id ? action.userBillingInfo : bi,
        );
        return;
      case actionTypes.SET_USER_BILLING_INFO:
        draft.userBillingInfo = action.userBillingInfo;
        draft.userBillingInfoFetched = true;
        draft.fetchingUserBillingInfo = false;
        return;
      case actionTypes.ADD_USER_BILLING_INFO:
        draft.userBillingInfo = [...state.userBillingInfo, action.userBillingInfo];
        return;
      case actionTypes.SET_USER_BILLING_INFO_FETCHING:
        draft.fetchingUserBillingInfo = action.fetching;
        return;
      default:
        return;
    }
  });
};

export default billing;
